import type { ReactElement, SVGProps } from 'react';

/**
 * Icons.
 *
 * Hand-drawn on a 24px grid, 1.5px stroke, no fills. Emoji are not interface
 * icons and no icon library is installed, so this file is the whole set —
 * eleven glyphs, added to only when a screen genuinely needs one.
 *
 * Every icon is decorative: it sits beside a text label and is hidden from
 * assistive technology. Nothing in this app is identified by picture alone.
 */

export type IconName =
  | 'today'
  | 'enquiries'
  | 'bookings'
  | 'calendar'
  | 'fleet'
  | 'drivers'
  | 'rates'
  | 'invoices'
  | 'dues'
  | 'money'
  | 'settings'
  | 'plus'
  | 'attention';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & { readonly name: IconName };

const PATHS: Readonly<Record<IconName, ReactElement>> = {
  today: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  enquiries: (
    <>
      <path d="M4.5 6.75h15v9.5H12l-4 3v-3H4.5z" />
      <path d="M8 10.5h8M8 13h5" />
    </>
  ),
  bookings: (
    <>
      <path d="M5.5 4.5h13v15h-13z" />
      <path d="M8.5 9h7M8.5 12.5h7M8.5 16h4" />
    </>
  ),
  calendar: (
    <>
      <path d="M4.5 6.5h15v13h-15z" />
      <path d="M4.5 10.5h15M9 4.5v4M15 4.5v4" />
      <path d="M8 14h3v3H8z" />
    </>
  ),
  fleet: (
    <>
      <path d="M3.5 14.5h17M5 14.5l1.75-5.25A2 2 0 0 1 8.65 8h6.7a2 2 0 0 1 1.9 1.25L19 14.5" />
      <path d="M3.5 14.5v3.25h2.25M20.5 14.5v3.25h-2.25" />
      <circle cx="7.25" cy="17.75" r="1.25" />
      <circle cx="16.75" cy="17.75" r="1.25" />
    </>
  ),
  drivers: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5.5 19.5c0-3.31 2.91-6 6.5-6s6.5 2.69 6.5 6" />
    </>
  ),
  rates: (
    <>
      <path d="M12.5 4.5H19.5v7L11 20l-7-7z" />
      <circle cx="16" cy="8" r="1.25" />
    </>
  ),
  invoices: (
    <>
      <path d="M6 3.5h9l3.5 3.5v13H6z" />
      <path d="M14.5 3.5V7.5h4" />
      <path d="M9 11.5h6M9 14.5h6M9 17h3.5" />
    </>
  ),
  dues: (
    <>
      <ellipse cx="12" cy="7" rx="6.5" ry="2.5" />
      <path d="M5.5 7v5c0 1.38 2.91 2.5 6.5 2.5s6.5-1.12 6.5-2.5V7" />
      <path d="M5.5 12v4c0 1.38 2.91 2.5 6.5 2.5s6.5-1.12 6.5-2.5v-4" />
    </>
  ),
  money: (
    <>
      <path d="M4.5 19.5V4.5" />
      <path d="M4.5 19.5h15" />
      <path d="M8 16.5v-4M12 16.5V8M16 16.5v-6" />
    </>
  ),
  settings: (
    <>
      <path d="M4.5 8h15M4.5 16h15" />
      <circle cx="9.5" cy="8" r="2" />
      <circle cx="15" cy="16" r="2" />
    </>
  ),
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  attention: (
    <>
      <path d="M12 4.75 20 18.5H4z" />
      <path d="M12 10v3.75M12 16h.01" />
    </>
  ),
};

export function Icon({ name, ...rest }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
